import React, { useState, useCallback } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { authFetch, API_BASE } from "../api";
import "./SchedulePanel.css";

function extractAppName(event) {
  // Try appVars first
  if (event.appVars) {
    const appUrlVar = event.appVars.find((v) => v.name === "app_url");
    if (appUrlVar) {
      const match = appUrlVar.value.match(/\/preview\/([^/]+)/);
      if (match) return match[1];
    }
  }
  // Try event name
  if (event.name) {
    const forgeMatch = event.name.match(/Forge(?:\s+Studio)?:\s*(.+)/i);
    if (forgeMatch) return forgeMatch[1].trim();
  }
  // Fallback to first asset name
  if (event.assets && event.assets[0]) {
    return event.assets[0].name || "Unknown";
  }
  return event.name || "Unknown";
}

function formatDuration(ms) {
  if (!ms) return "—";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString();
}

function SortableEvent({ event, onEdit, onDelete, onSetAppVar, onRemoveAppVar, cmsAuth, groupId }) { // eslint-disable-line no-unused-vars
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: event.uuid || event.id });
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  // Local UI state for the "+ Add variable" form. Keeps the user's typing
  // out of the parent's events array until they actually commit a name.
  const [newVarName, setNewVarName] = useState("");
  const [newVarValue, setNewVarValue] = useState("");

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const appName = extractAppName(event);
  const assetType = event.assets?.[0]?.type === "apk" ? "APK" : "URL";
  const duration = event.assets?.[0]?.durationMs || event.durationMs;

  const startEdit = (field, currentValue) => {
    setEditing(field);
    setEditValue(currentValue || "");
  };

  const saveEdit = () => {
    if (editing && onEdit) {
      onEdit(event, editing, editValue);
    }
    setEditing(null);
  };

  return (
    <div ref={setNodeRef} style={style} className="schedule-event">
      <div className="schedule-event-drag" {...attributes} {...listeners}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
          <circle cx="4" cy="2" r="1" /><circle cx="8" cy="2" r="1" />
          <circle cx="4" cy="6" r="1" /><circle cx="8" cy="6" r="1" />
          <circle cx="4" cy="10" r="1" /><circle cx="8" cy="10" r="1" />
        </svg>
      </div>

      <div className="schedule-event-info">
        <div className="schedule-event-top">
          <span className={`schedule-event-type ${assetType.toLowerCase()}`}>{assetType}</span>
          <span className="schedule-event-name">{appName}</span>
        </div>
        <div className="schedule-event-details">
          <span className="schedule-event-detail" onClick={() => startEdit("duration", duration ? String(Math.round(duration / 1000)) : "")}>
            Duration: {editing === "duration" ? (
              <input
                className="schedule-inline-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={saveEdit}
                onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                autoFocus
                size={4}
              />
            ) : (
              <strong>{formatDuration(duration)}</strong>
            )}
          </span>
          <span className="schedule-event-detail" onClick={() => startEdit("priority", String(event.priority || 100))}>
            Priority: {editing === "priority" ? (
              <select
                className="schedule-inline-select"
                value={editValue}
                onChange={(e) => { setEditValue(e.target.value); }}
                onBlur={saveEdit}
                autoFocus
              >
                <option value="100">Normal</option>
                <option value="200">High</option>
                <option value="300">Urgent</option>
              </select>
            ) : (
              <strong>{event.priority === 300 ? "Urgent" : event.priority === 200 ? "High" : "Normal"}</strong>
            )}
          </span>
          <span className="schedule-event-detail">
            {formatDate(event.startTime)} → {formatDate(event.endTime)}
          </span>
        </div>

        {/* App variables — editable per-event. Skip app_url because it
            is set automatically from the asset's URL by the publish flow
            and editing it here would break the link. Each value is a
            controlled-input pattern with defaultValue + onBlur save, so
            keystrokes don't touch the parent state until commit. */}
        {(() => {
          const userVars = (event.appVars || []).filter((v) => v.name !== "app_url");
          return (
            <div className="schedule-event-vars">
              <div className="schedule-event-vars-label">Variables</div>
              {userVars.map((v) => (
                <div key={v.name} className="schedule-event-var-row">
                  <span className="schedule-event-var-name">{v.name}</span>
                  <span className="schedule-event-var-eq">=</span>
                  <input
                    className="schedule-event-var-input"
                    defaultValue={v.value || ""}
                    onBlur={(e) => {
                      const next = e.target.value;
                      if (next !== (v.value || "")) onSetAppVar(event, v.name, next);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.target.blur();
                      if (e.key === "Escape") { e.target.value = v.value || ""; e.target.blur(); }
                    }}
                  />
                  <button
                    className="schedule-event-var-remove"
                    title="Remove variable"
                    onClick={() => {
                      if (window.confirm(`Remove "${v.name}" from this event?`)) {
                        onRemoveAppVar(event, v.name);
                      }
                    }}
                  >×</button>
                </div>
              ))}
              <div className="schedule-event-var-row schedule-event-var-add-row">
                <input
                  className="schedule-event-var-input schedule-event-var-name-input"
                  placeholder="name"
                  value={newVarName}
                  onChange={(e) => setNewVarName(e.target.value)}
                />
                <span className="schedule-event-var-eq">=</span>
                <input
                  className="schedule-event-var-input"
                  placeholder="value"
                  value={newVarValue}
                  onChange={(e) => setNewVarValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newVarName.trim()) {
                      onSetAppVar(event, newVarName.trim(), newVarValue);
                      setNewVarName(""); setNewVarValue("");
                    }
                  }}
                />
                <button
                  className="schedule-event-var-add"
                  disabled={!newVarName.trim()}
                  onClick={() => {
                    if (newVarName.trim()) {
                      onSetAppVar(event, newVarName.trim(), newVarValue);
                      setNewVarName(""); setNewVarValue("");
                    }
                  }}
                >+ Add</button>
              </div>
            </div>
          );
        })()}
      </div>

      <button className="schedule-event-delete" onClick={() => onDelete(event)} title="Remove from schedule">
        &times;
      </button>
    </div>
  );
}

export default function SchedulePanel({ deviceId, deviceName, events: initialEvents, cmsAuth, groupId, onEventsChanged }) {
  const [events, setEvents] = useState(initialEvents || []);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(async (dndEvent) => {
    const { active, over } = dndEvent;
    if (!over || active.id === over.id) return;

    const oldIndex = events.findIndex((e) => (e.uuid || e.id) === active.id);
    const newIndex = events.findIndex((e) => (e.uuid || e.id) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const previousOrder = events;
    const newOrder = arrayMove(events, oldIndex, newIndex);
    setEvents(newOrder); // optimistic — snap to new position immediately

    // CMS reorder is scoped to a specific schedule day. We derive the day
    // from the dragged event's startTime → local midnight → UTC ISO, matching
    // the format the CMS UI sends (e.g. "2026-04-24T04:00:00.000Z" = midnight
    // EDT). Events in the panel that don't belong to this day aren't handled
    // here — future iteration could group events by day and only DnD within
    // a group.
    const draggedEvent = events[oldIndex];
    const startTime = draggedEvent?.startTime || new Date().toISOString();
    const localMidnight = new Date(startTime);
    localMidnight.setHours(0, 0, 0, 0);
    const dateIso = localMidnight.toISOString();

    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/videri/events/order`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-videri-token": cmsAuth.token,
          "x-tenant": cmsAuth.tenant,
          "x-videri-env": cmsAuth.environment || "dev",
        },
        body: JSON.stringify({
          canvasId: deviceId,
          date: dateIso,
          order: newOrder.map((e) => e.uuid || e.id),
          groupId,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Reorder failed (${res.status})`);
      }
      if (onEventsChanged) onEventsChanged();
    } catch (err) {
      console.error("Failed to save event order:", err);
      alert(`Could not save new order: ${err.message}`);
      setEvents(previousOrder); // rollback on failure
    } finally {
      setSaving(false);
    }
  }, [events, deviceId, groupId, cmsAuth, onEventsChanged]);

  const handleDelete = useCallback(async (evt) => {
    if (!window.confirm(`Remove "${extractAppName(evt)}" from ${deviceName}'s schedule?`)) return;
    const eventId = evt.uuid || evt.id;
    try {
      await authFetch(`${API_BASE}/videri/events/${eventId}?groupId=${groupId}`, {
        method: "DELETE",
        headers: {
          "x-videri-token": cmsAuth.token,
          "x-tenant": cmsAuth.tenant,
          "x-videri-env": cmsAuth.environment || "dev",
        },
      });
      setEvents((prev) => prev.filter((e) => (e.uuid || e.id) !== eventId));
      if (onEventsChanged) onEventsChanged();
    } catch (err) {
      console.error("Failed to delete event:", err);
    }
  }, [cmsAuth, groupId, deviceName, onEventsChanged]);

  // Build the full PATCH body the CMS expects. PATCH /publisher/api/v1/events/{id}
  // is full-replacement despite the verb — any field omitted resets to a
  // server default. We spread the existing event (which has all the fields
  // we need) and overlay whatever the caller wants to change.
  const buildPatchBody = useCallback((evt, changes) => {
    const merged = { ...evt, ...changes };
    return {
      canvasesIds: merged.canvasesIds || merged.canvasIds || [],
      priority: merged.priority ?? 100,
      frequency: merged.frequency || { type: "daily" },
      startTime: merged.startTime,
      endTime: merged.endTime,
      durationMs: merged.durationMs ?? merged.assets?.[0]?.durationMs ?? 15000,
      appVars: Array.isArray(merged.appVars) ? merged.appVars : [],
      itemsPerPass: merged.itemsPerPass ?? -1,
      isSlot: !!merged.isSlot,
      isUtc: !!merged.isUtc,
      selectedBlob: merged.selectedBlob || "",
      muted: !!merged.muted,
    };
  }, []);

  // Persist a single change to one event. Optimistically updates local
  // state, calls PATCH, rolls back on failure.
  const patchEvent = useCallback(async (evt, changes, optimisticPatch) => {
    const eventId = evt.uuid || evt.id;
    const previous = events;
    if (optimisticPatch) {
      setEvents((prev) => prev.map((e) =>
        (e.uuid || e.id) === eventId ? { ...e, ...optimisticPatch } : e
      ));
    }
    setSaving(true);
    try {
      const body = buildPatchBody(evt, changes);
      const res = await authFetch(
        `${API_BASE}/videri/events/${eventId}?groupId=${encodeURIComponent(groupId)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "x-videri-token": cmsAuth.token,
            "x-tenant": cmsAuth.tenant,
            "x-videri-env": cmsAuth.environment || "dev",
          },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Update failed (${res.status})`);
      }
      // Re-sync with the canonical event the server returned (handles
      // cases where the server normalizes / fills derived fields).
      const data = await res.json().catch(() => null);
      if (data?.event) {
        setEvents((prev) => prev.map((e) =>
          (e.uuid || e.id) === eventId ? { ...e, ...data.event } : e
        ));
      }
      if (onEventsChanged) onEventsChanged();
    } catch (err) {
      console.error("Failed to update event:", err);
      alert(`Could not save change: ${err.message}`);
      setEvents(previous);
    } finally {
      setSaving(false);
    }
  }, [events, groupId, cmsAuth, onEventsChanged, buildPatchBody]);

  const handleEdit = useCallback((evt, field, value) => {
    if (field === "duration") {
      const ms = parseInt(value, 10) * 1000;
      if (!Number.isFinite(ms) || ms <= 0) return;
      const patched = { ...evt, durationMs: ms, assets: evt.assets?.map((a) => ({ ...a, durationMs: ms })) };
      patchEvent(evt, { durationMs: ms }, patched);
    } else if (field === "priority") {
      const n = parseInt(value, 10);
      if (!Number.isFinite(n)) return;
      patchEvent(evt, { priority: n }, { priority: n });
    }
  }, [patchEvent]);

  // Mutate a single appVar entry's value. Used by the inline editor in
  // SortableEvent. Adds entry if name is new, updates if existing.
  const setAppVar = useCallback((evt, name, value) => {
    const current = Array.isArray(evt.appVars) ? evt.appVars : [];
    const existingIdx = current.findIndex((v) => v.name === name);
    let next;
    if (existingIdx >= 0) {
      next = current.map((v, i) => (i === existingIdx ? { ...v, value } : v));
    } else {
      next = [...current, { name, value }];
    }
    patchEvent(evt, { appVars: next }, { appVars: next });
  }, [patchEvent]);

  const removeAppVar = useCallback((evt, name) => {
    const current = Array.isArray(evt.appVars) ? evt.appVars : [];
    const next = current.filter((v) => v.name !== name);
    patchEvent(evt, { appVars: next }, { appVars: next });
  }, [patchEvent]);

  const eventIds = events.map((e) => e.uuid || e.id);

  return (
    <div className="schedule-panel">
      <div className="schedule-panel-header">
        <h4>Schedule — {deviceName}</h4>
        <span className="schedule-panel-count">{events.length} events</span>
      </div>

      {events.length === 0 ? (
        <div className="schedule-panel-empty">No events scheduled for this device.</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={eventIds} strategy={verticalListSortingStrategy}>
            <div className="schedule-event-list">
              {events.map((evt) => (
                <SortableEvent
                  key={evt.uuid || evt.id}
                  event={evt}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onSetAppVar={setAppVar}
                  onRemoveAppVar={removeAppVar}
                  cmsAuth={cmsAuth}
                  groupId={groupId}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

export { extractAppName };
